import { runPrompt, linkAccount as apiLinkAccount, checkLinked } from '@/api/ai-agent.api'
import {
  getStoredAccountId,
  setStoredAccountId,
  getLinkedAccounts,
  setLinkedAccounts,
  getStoredSessions,
  saveStoredSessions
} from '@/api/ai-agent.storage'

// The in-flight request's AbortController is intentionally kept out of Vuex
// state. It is not serializable, and the dev logger/strict plugins would try
// to clone it on every mutation. State only needs the isStreaming flag.
let abortController = null

// Optional seed for the Ringba account (local dev / e2e only; not committed in
// any deployed env file). Each admin links their own account in the panel and
// the choice persists per browser (localStorage).
const CONFIGURED_ACCOUNT_ID = process.env.VUE_APP_RINGBA_ACCOUNT_ID || ''

function makeId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return Math.random().toString(36).slice(2, 10)
}

// A session's title is just its first user prompt, trimmed to one line so the
// history list stays scannable.
function deriveTitle(messages) {
  const firstUser = (messages || []).find(m => m.role === 'user')
  const text = (firstUser && firstUser.text ? firstUser.text : '').trim()
  if (!text) {
    return 'New chat'
  }

  const oneLine = text.replace(/\s+/g, ' ')
  return oneLine.length > 60 ? oneLine.slice(0, 59) + '…' : oneLine
}

// The history list only needs lightweight metadata; the full message array
// lives in storage and is read on demand when a session is opened.
function toMeta(session) {
  return {
    id: session.id,
    title: session.title || 'New chat',
    createdAt: session.createdAt,
    updatedAt: session.updatedAt,
    messageCount: Array.isArray(session.messages) ? session.messages.length : (session.messageCount || 0)
  }
}

// Every assistant message is created with the same shape so templates can
// rely on arrays existing without optional-chaining everywhere.
function newAssistantMessage(now) {
  return {
    id: makeId(),
    role: 'assistant',
    plan: '',
    tools: [],
    answer: '',
    citations: [],
    at: now,
    lastAt: null,
    status: 'streaming',
    error: null
  }
}

function createState() {
  return {
    panelOpen: false,
    messages: [],
    isStreaming: false,
    accountId: getStoredAccountId() || CONFIGURED_ACCOUNT_ID,
    // Accounts this browser has linked a token for. Gates the "Step 1: link
    // account" screen. The worker re-checks on every query, so this is just a
    // UI hint, reconciled with the server by verifyAccountLink.
    linkedAccounts: getLinkedAccounts(),
    // Submitted prompts, oldest first, for shell-style ↑/↓ recall in the
    // composer. Survives clearChat on purpose, like real terminal history.
    promptHistory: [],
    // Identity used to scope saved sessions to the logged-in admin. Resolved
    // lazily from the auth module when the panel opens.
    userId: '',
    // The session the current `messages` belong to. null means "unsaved /
    // brand-new chat" until the first prompt creates one.
    currentSessionId: null,
    // Lightweight metadata for the history list: { id, title, createdAt,
    // updatedAt, messageCount }. Full transcripts are read from storage.
    sessions: []
  }
}

const state = createState()

// Mutations are pure — no localStorage, no network. Side effects live in
// actions. This keeps dev-mode strict-mode happy and makes mutations
// trivially testable in isolation.
const mutations = {
  setPanelOpen(state, open) {
    state.panelOpen = open
  },
  togglePanel(state) {
    state.panelOpen = !state.panelOpen
  },
  setAccountId(state, value) {
    state.accountId = value || ''
  },
  setLinkedAccounts(state, list) {
    state.linkedAccounts = Array.isArray(list) ? list : []
  },
  pushMessage(state, message) {
    state.messages.push(message)
  },
  patchMessage(state, { id, patch }) {
    const idx = state.messages.findIndex(m => m.id === id)
    if (idx === -1) {
      return
    }

    state.messages.splice(idx, 1, { ...state.messages[idx], ...patch })
  },
  appendPlan(state, { id, text }) {
    const idx = state.messages.findIndex(m => m.id === id)
    if (idx === -1) {
      return
    }

    const msg = state.messages[idx]
    state.messages.splice(idx, 1, { ...msg, plan: (msg.plan || '') + text })
  },
  startTool(state, { id, toolId, name, args }) {
    const idx = state.messages.findIndex(m => m.id === id)
    if (idx === -1) {
      return
    }

    const msg = state.messages[idx]
    state.messages.splice(idx, 1, {
      ...msg,
      tools: [...msg.tools, { id: toolId, name, args, result: null, status: 'running' }]
    })
  },
  finishTool(state, { id, toolId, result }) {
    const idx = state.messages.findIndex(m => m.id === id)
    if (idx === -1) {
      return
    }

    const msg = state.messages[idx]
    const tools = msg.tools.map(t => t.id === toolId ? { ...t, result, status: 'done' } : t)
    state.messages.splice(idx, 1, { ...msg, tools })
  },
  appendAnswer(state, { id, text }) {
    const idx = state.messages.findIndex(m => m.id === id)
    if (idx === -1) {
      return
    }

    const msg = state.messages[idx]
    state.messages.splice(idx, 1, { ...msg, answer: (msg.answer || '') + text })
  },
  setCitations(state, { id, items }) {
    const idx = state.messages.findIndex(m => m.id === id)
    if (idx === -1) {
      return
    }

    const msg = state.messages[idx]
    state.messages.splice(idx, 1, { ...msg, citations: Array.isArray(items) ? items : [] })
  },
  setStreaming(state, value) {
    state.isStreaming = value
  },
  clearMessages(state) {
    state.messages = []
  },
  setMessages(state, list) {
    state.messages = Array.isArray(list) ? list : []
  },
  setUserId(state, id) {
    state.userId = id || ''
  },
  setCurrentSessionId(state, id) {
    state.currentSessionId = id || null
  },
  setSessions(state, list) {
    state.sessions = Array.isArray(list) ? list : []
  },
  upsertSessionMeta(state, meta) {
    if (!Array.isArray(state.sessions)) {
      state.sessions = []
    }

    const idx = state.sessions.findIndex(s => s.id === meta.id)
    if (idx === -1) {
      state.sessions.unshift(meta)
    } else {
      state.sessions.splice(idx, 1, { ...state.sessions[idx], ...meta })
    }
  },
  pushHistory(state, text) {
    const trimmed = (text || '').trim()
    // Skip blanks and consecutive duplicates so ↑ doesn't stutter on repeats.
    if (!trimmed || state.promptHistory[state.promptHistory.length - 1] === trimmed) {
      return
    }

    state.promptHistory.push(trimmed)
    // Keep only the most recent 100 entries.
    if (state.promptHistory.length > 100) {
      state.promptHistory.shift()
    }
  }
}

// Keep requests bounded: only the most recent turns go to the worker. Older
// context is dropped first; the full transcript stays in the UI/session store.
const MAX_THREAD_HISTORY_MESSAGES = 20

function buildThreadHistory(messages) {
  const history = []
  for (const msg of messages) {
    if (msg.role === 'user') {
      history.push({ role: 'user', content: msg.text })
    } else if (msg.role === 'assistant') {
      const text = (msg.plan ? msg.plan + '\n\n' : '') + (msg.answer || '')
      if (text.trim()) {
        history.push({ role: 'assistant', content: text.trim() })
      }
    }
  }

  return history.slice(-MAX_THREAD_HISTORY_MESSAGES)
}

const actions = {
  openPanel({ commit, dispatch }) {
    dispatch('syncUser')
    dispatch('verifyAccountLink')
    commit('setPanelOpen', true)
  },
  closePanel({ commit }) {
    commit('setPanelOpen', false)
  },
  togglePanel({ commit, state, dispatch }) {
    commit('togglePanel')
    if (state.panelOpen) {
      dispatch('syncUser')
      dispatch('verifyAccountLink')
    }
  },

  // Resolve the logged-in admin and load *their* saved sessions. When the user
  // changes (or first resolves), drop any in-memory conversation so one admin
  // never sees another's chat carried over in the same tab.
  syncUser({ commit, state, dispatch, rootGetters }) {
    const uid = rootGetters['auth/loggedInUserID'] || ''
    if (uid === state.userId) {
      return
    }

    commit('setUserId', uid)
    commit('clearMessages')
    commit('setCurrentSessionId', null)
    dispatch('loadUserSessions')
  },

  loadUserSessions({ commit, state }) {
    commit('setSessions', getStoredSessions(state.userId).map(toMeta))
  },

  // Make sure there's a session id to attach the current exchange to. Kept as
  // an action (not an inline commit in sendPrompt) so unit tests that stub
  // `dispatch` stay isolated from session/storage side effects.
  ensureSession({ commit, state, rootGetters }, firstPrompt) {
    if (!state.userId) {
      commit('setUserId', rootGetters['auth/loggedInUserID'] || '')
    }

    if (state.currentSessionId) {
      return
    }

    const now = Date.now()
    const id = makeId()
    commit('setCurrentSessionId', id)
    commit('upsertSessionMeta', {
      id,
      title: deriveTitle([{ role: 'user', text: firstPrompt }]),
      createdAt: now,
      updatedAt: now,
      messageCount: 0
    })
  },

  // Write the current conversation back to the user's session store. Called at
  // the end of each completed exchange, so a reload between turns keeps history.
  persistCurrentSession({ commit, state }) {
    if (!state.currentSessionId || !state.messages.length) {
      return
    }

    const all = getStoredSessions(state.userId)
    const existing = all.find(s => s.id === state.currentSessionId)
    const session = {
      id: state.currentSessionId,
      title: deriveTitle(state.messages),
      createdAt: existing ? existing.createdAt : Date.now(),
      updatedAt: Date.now(),
      messages: state.messages
    }

    const next = [session, ...all.filter(s => s.id !== state.currentSessionId)]
    const saved = saveStoredSessions(state.userId, next)
    commit('setSessions', saved.map(toMeta))
  },

  loadSession({ commit, state }, id) {
    if (abortController) {
      abortController.abort()
    }

    commit('setStreaming', false)

    const session = getStoredSessions(state.userId).find(s => s.id === id)
    if (!session) {
      return
    }

    // A transcript saved mid-stream could carry a 'streaming' status that would
    // otherwise spin forever once reloaded — settle those to 'done'.
    const messages = (session.messages || []).map(m =>
      m.role === 'assistant' && m.status === 'streaming'
        ? { ...m, status: 'done', lastAt: m.lastAt || m.at || Date.now() }
        : m
    )

    commit('setMessages', messages)
    commit('setCurrentSessionId', id)
  },

  deleteSession({ commit, state }, id) {
    const remaining = getStoredSessions(state.userId).filter(s => s.id !== id)
    const saved = saveStoredSessions(state.userId, remaining)
    commit('setSessions', saved.map(toMeta))

    if (state.currentSessionId === id) {
      commit('clearMessages')
      commit('setCurrentSessionId', null)
    }
  },

  // Change the Ringba account the worker queries and remember it for next time.
  setAccount({ commit, dispatch }, value) {
    const v = (value || '').trim()
    commit('setAccountId', v)
    setStoredAccountId(v)
    // Re-check whether the newly selected account already has a token, so the
    // link gate shows (or stays hidden) correctly without a reload.
    dispatch('verifyAccountLink')
  },

  // Link a Ringba account + token (browser equivalent of Slack's /ringba-link).
  // On success: make it the active account and mark it linked. Returns
  // { ok, error? } so the panel can show inline validation.
  async linkAccount({ commit, state, rootGetters, dispatch }, { accountId, apiToken } = {}) {
    const id = (accountId || '').trim()
    const token = (apiToken || '').trim()
    if (!id || !token) {
      return { ok: false, error: 'Account ID and API token are required.' }
    }

    const result = await apiLinkAccount({
      accountId: id,
      apiToken: token,
      accessToken: rootGetters['auth/accessToken'],
      refreshAccessToken: async () => {
        await dispatch('auth/refreshAccessToken', null, { root: true })
        return rootGetters['auth/accessToken']
      }
    })

    if (!result.ok) {
      return result
    }

    commit('setAccountId', id)
    setStoredAccountId(id)

    const list = [...new Set([...state.linkedAccounts, id])]
    commit('setLinkedAccounts', list)
    setLinkedAccounts(list)

    return { ok: true }
  },

  // Reconcile the local "linked" hint with the worker for the active account.
  // Lets a script-seeded account skip the gate, and drops a revoked one. On any
  // network/auth hiccup it leaves the local list untouched (ok=false).
  async verifyAccountLink({ commit, state, rootGetters, dispatch }) {
    if (!state.accountId) {
      return
    }

    const { linked, ok } = await checkLinked({
      accountId: state.accountId,
      accessToken: rootGetters['auth/accessToken'],
      refreshAccessToken: async () => {
        await dispatch('auth/refreshAccessToken', null, { root: true })
        return rootGetters['auth/accessToken']
      }
    })

    if (!ok) {
      return
    }

    const set = new Set(state.linkedAccounts)
    if (linked) {
      set.add(state.accountId)
    } else {
      set.delete(state.accountId)
    }

    const list = [...set]
    commit('setLinkedAccounts', list)
    setLinkedAccounts(list)
  },

  // "New chat" — the previous conversation is already saved to history at each
  // completed exchange, so this just resets the live thread to a blank session.
  clearChat({ commit }) {
    if (abortController) {
      abortController.abort()
    }

    commit('clearMessages')
    commit('setCurrentSessionId', null)
  },
  stopStreaming() {
    if (abortController) {
      abortController.abort()
    }
  },

  async sendPrompt({ commit, state, rootGetters, dispatch }, prompt) {
    const text = (prompt || '').trim()
    if (!text || state.isStreaming) {
      return
    }

    commit('pushHistory', text)

    // Attach this exchange to a session (creating one on the first prompt).
    await dispatch('ensureSession', text)

    const now = Date.now()
    commit('pushMessage', { id: makeId(), role: 'user', text, at: now })

    const assistant = newAssistantMessage(now)
    commit('pushMessage', assistant)

    abortController = new AbortController()
    commit('setStreaming', true)

    const threadHistory = buildThreadHistory(state.messages.slice(0, -2))

    // The worker always emits `done` after an `error` (its finally block). Track
    // whether we errored so the trailing `done` doesn't overwrite the error
    // status — otherwise the failure is swallowed and the user sees a blank reply.
    let errored = false

    await runPrompt(text, {
      signal: abortController.signal,
      accountId: state.accountId,
      threadHistory,
      accessToken: rootGetters['auth/accessToken'],
      refreshAccessToken: async () => {
        await dispatch('auth/refreshAccessToken', null, { root: true })
        return rootGetters['auth/accessToken']
      },
      onEvent: (ev) => {
        switch (ev.type) {
          case 'plan_chunk':
            commit('appendPlan', { id: assistant.id, text: ev.text })
            break
          case 'tool_start':
            commit('startTool', { id: assistant.id, toolId: ev.id, name: ev.name, args: ev.args })
            break
          case 'tool_result':
            commit('finishTool', { id: assistant.id, toolId: ev.id, result: ev.result })
            break
          case 'answer_chunk':
            commit('appendAnswer', { id: assistant.id, text: ev.text })
            break
          case 'citations':
            commit('setCitations', { id: assistant.id, items: ev.items })
            break
          case 'done':
            if (!errored) {
              commit('patchMessage', {
                id: assistant.id,
                patch: { status: 'done', lastAt: Date.now() }
              })
            }

            break
          case 'error':
            errored = true
            commit('patchMessage', {
              id: assistant.id,
              patch: { status: 'error', error: ev.message, lastAt: Date.now() }
            })
            break
        }
      }
    })

    commit('setStreaming', false)
    abortController = null

    // Save the completed exchange so it survives reloads and shows in history.
    dispatch('persistCurrentSession')
  }
}

const getters = {
  hasMessages: state => state.messages.length > 0,
  hasAccount: state => Boolean(state.accountId),
  isAccountLinked: state => Boolean(state.accountId) && state.linkedAccounts.includes(state.accountId),
  sortedSessions: state => [...(state.sessions || [])].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)),
  lastMessageSignature: state => {
    // Cheap fingerprint a watcher can subscribe to without going deep.
    // Changes whenever the streaming tip grows. O(1) per mutation.
    if (state.messages.length === 0) {
      return ''
    }

    const last = state.messages[state.messages.length - 1]
    const planLen = (last.plan || '').length
    const ansLen = (last.answer || '').length
    const toolCount = (last.tools || []).length
    return `${state.messages.length}|${last.id}|${last.status}|${planLen}|${ansLen}|${toolCount}`
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
